import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, TextField, Button, Paper, CircularProgress } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SettingsIcon from '@mui/icons-material/Settings';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import axios from 'axios';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuItem from '@mui/material/MenuItem';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import Autocomplete from '@mui/material/Autocomplete';
import api from '../services/api';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

// Streaming Ollama response (token by token)
async function proxyIaApiRequest({ target_url, payload, headers = {} }) {
  // Utilitaire pour passer par le backend
  const res = await fetch(process.env.REACT_APP_API_URL + '/api/proxy-ia', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ target_url, payload, headers })
  });
  if (!res.ok) throw new Error('Erreur proxy IA: ' + res.status);
  return res;
}

// Version proxy du streaming Ollama
async function streamOllamaResponse({ prompt, model = 'llama3', aiUrl = 'http://localhost:11434/api/chat', onToken, onDone, onError }) {
  try {
    const res = await proxyIaApiRequest({
      target_url: aiUrl,
      payload: {
        model,
        messages: [{ role: 'user', content: prompt }],
        stream: true
      },
      headers: { 'Content-Type': 'application/json' }
    });
    if (!res.body) throw new Error('No response body');
    const reader = res.body.getReader();
    let partial = '';
    const decoder = new TextDecoder();
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      for (const line of chunk.split('\n')) {
        if (!line.trim()) continue;
        try {
          const obj = JSON.parse(line);
          const token = obj.message?.content || obj.response || '';
          if (token) {
            partial += token;
            if (onToken) onToken(token, partial);
          }
        } catch (e) {
          // Ignore parse errors (incomplete JSON)
        }
      }
    }
    if (onDone) onDone(partial);
  } catch (err) {
    if (onError) onError(err);
  }
}

// Configuration de base pour Ollama (adapter si besoin)
const OLLAMA_API_URL = process.env.REACT_APP_OLLAMA_API_URL || 'http://localhost:11434/api/generate';
const OLLAMA_MODEL = process.env.REACT_APP_OLLAMA_MODEL || 'llama3';

const INPUT_OPTIONS = [
  { value: 'custom', label: 'Texte personnalisé' },
  { value: 'prev_output', label: 'Sortie du prompt précédent' },
  { value: 'file', label: 'Fichier' }
];
const OUTPUT_OPTIONS = [
  { value: 'text', label: 'Texte brut' },
  { value: 'json', label: 'JSON (si possible)' }
];

const IA_MODELS = [
  { label: 'Ollama (local)', value: 'ollama', aiUrl: OLLAMA_API_URL, models: ['llama3', 'mistral', 'gemma', 'phi3', 'mixtral', 'codellama'] },
  { label: 'OpenAI GPT-3.5/4', value: 'openai', aiUrl: process.env.REACT_APP_OPENAI_API_URL || '', models: ['gpt-3.5-turbo', 'gpt-4', 'gpt-4o'] },
  { label: 'Mistral (API)', value: 'mistral', aiUrl: process.env.REACT_APP_MISTRAL_API_URL || '', models: ['mistral-tiny', 'mistral-small', 'mistral-medium'] },
  { label: 'Gemini (Google)', value: 'gemini', aiUrl: process.env.REACT_APP_GEMINI_API_URL || '', models: ['gemini-pro'] }
];

function getUserId() {
  const userStr = window.localStorage.getItem('user');
  if (!userStr) return null;
  try {
    const user = JSON.parse(userStr);
    return user.uuid7 || user.uuid || user.id || null;
  } catch {
    return null;
  }
}

async function saveIaConfigForUser({ iaProvider, iaModel, customApiUrl, key }) {
  const uuid7 = getUserId();
  if (!uuid7) return;
  const token = window.localStorage.getItem('token');
  try {
    await api.post('/api/config/user/ia-config', { uuid7, iaProvider, iaModel, customApiUrl, key }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch {}
}

async function loadIaConfigForUser() {
  const uuid7 = getUserId();
  if (!uuid7) return null;
  const token = window.localStorage.getItem('token');
  try {
    const res = await api.get(`/api/config/configs/${uuid7}?prefix=ia_config`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const configs = res.data
      .filter(cfg => cfg.key === 'ia_config_global' || cfg.key.startsWith('ia_config_'))
      .map(cfg => {
        try {
          return { ...JSON.parse(cfg.value), key: cfg.key, id: cfg.id };
        } catch {
          return { key: cfg.key, id: cfg.id };
        }
      });
    return configs;
  } catch {
    return null;
  }
}

// Fonction pour charger dynamiquement les modèles Ollama
async function fetchOllamaModels(aiUrl = OLLAMA_API_URL) {
  try {
    const url = aiUrl.replace(/\/generate$/, '/tags');
    // Ne pas faire d'appel POST si le backend cible est 4styl.com (refuse payload vide)
    if (aiUrl.includes('4styl.com')) return [];
    const res = await proxyIaApiRequest({
      target_url: url,
      payload: {},
      headers: { 'Content-Type': 'application/json' }
    });
    if (!res.ok) throw new Error('Erreur chargement modèles Ollama');
    const data = await res.json();
    if (Array.isArray(data.models)) {
      return data.models.map(m => m.name || m.model || m.tag || m);
    }
    if (Array.isArray(data.tags)) {
      return data.tags.map(t => t.name || t.tag || t);
    }
    return [];
  } catch (e) {
    return [];
  }
}

export default function AgentApp() {
  const [tab, setTab] = useState(0); // 0: Agent, 1: Config
  // Cascade prompts state
  const [prompts, setPrompts] = useState([
    { input: '', output: '', loading: false, error: '', inputType: 'custom', outputType: 'text', prevPromptIdx: null }
  ]);
  const [iaProvider, setIaProvider] = useState(() => localStorage.getItem('ia_provider') || 'ollama');
  const [iaModel, setIaModel] = useState(() => localStorage.getItem('ia_model') || 'llama3');
  const [customApiUrl, setCustomApiUrl] = useState(() => localStorage.getItem('ia_api_url') || OLLAMA_API_URL);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);

  const [iaConfigs, setIaConfigs] = useState([]); // Liste des configs IA sauvegardées
  const [selectedConfigIdx, setSelectedConfigIdx] = useState(0);
  const [loadConfigsLoading, setLoadConfigsLoading] = useState(false);

  const [configName, setConfigName] = useState('');

  const [editingKey, setEditingKey] = useState(null);

  const fileInputRefs = useRef([]);

  const [ollamaModels, setOllamaModels] = useState([]);
  const [ollamaModelLoading, setOllamaModelLoading] = useState(false);

  // Explorer-like dialog to select a prompt session file from backend
  const [explorerOpen, setExplorerOpen] = useState(false);
  const [explorerPath, setExplorerPath] = useState('/');
  const [explorerFiles, setExplorerFiles] = useState([]);
  const [explorerLoading, setExplorerLoading] = useState(false);
  const [explorerError, setExplorerError] = useState('');

  // Ajoute l'explorateur pour sauvegarder la session de prompts
  const [saveExplorerOpen, setSaveExplorerOpen] = useState(false);
  const [saveExplorerPath, setSaveExplorerPath] = useState('/');
  const [saveExplorerFiles, setSaveExplorerFiles] = useState([]);
  const [saveExplorerLoading, setSaveExplorerLoading] = useState(false);
  const [saveExplorerError, setSaveExplorerError] = useState('');
  const [saveName, setSaveName] = useState(() => `prompts-${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.json`);

  async function fetchSaveExplorerFiles(path) {
    setSaveExplorerLoading(true);
    setSaveExplorerError('');
    try {
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      const token = window.localStorage.getItem('token');
      let relPath = path === '/' ? '' : path.replace(/^\//, '');
      const res = await api.get(`/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaveExplorerFiles(res.data.filter(f => f.type === 'folder' || f.type === 'file'));
    } catch {
      setSaveExplorerFiles([]);
    } finally {
      setSaveExplorerLoading(false);
    }
  }

  function handleOpenSaveExplorer() {
    setSaveExplorerOpen(true);
    setSaveExplorerPath('/');
    setSaveName(`prompts-${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.json`);
    fetchSaveExplorerFiles('/');
  }

  function handleSaveExplorerNavigate(folder) {
    const newPath = saveExplorerPath === '/' ? `/${folder}` : `${saveExplorerPath}/${folder}`;
    setSaveExplorerPath(newPath);
    fetchSaveExplorerFiles(newPath);
  }

  function handleSaveExplorerGoUp() {
    if (saveExplorerPath === '/') return;
    const parts = saveExplorerPath.split('/').filter(Boolean);
    parts.pop();
    const newPath = parts.length === 0 ? '/' : '/' + parts.join('/');
    setSaveExplorerPath(newPath);
    fetchSaveExplorerFiles(newPath);
  }

  async function handleSavePromptsToBackend() {
    const userStr = window.localStorage.getItem('user');
    let uuid7 = null;
    if (userStr) {
      try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
    }
    if (!uuid7) { alert('Utilisateur non trouvé'); return; }
    const token = window.localStorage.getItem('token');
    let relPath = saveExplorerPath === '/' ? saveName : saveExplorerPath.replace(/^\//, '') + '/' + saveName;
    try {
      await api.post('/save-file', {
        uuid7,
        path: relPath,
        content: JSON.stringify(prompts, null, 2),
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaveExplorerOpen(false);
      alert('Session de prompts sauvegardée !');
    } catch (e) {
      alert("Erreur lors de la sauvegarde des prompts : " + (e?.message || ''));
    }
  }

  useEffect(() => {
    setLoadingConfig(true);
    loadIaConfigForUser().then(cfg => {
      if (cfg && cfg.iaProvider) {
        setIaProvider(cfg.iaProvider);
        setIaModel(cfg.iaModel);
        setCustomApiUrl(cfg.customApiUrl);
        localStorage.setItem('ia_provider', cfg.iaProvider);
        localStorage.setItem('ia_model', cfg.iaModel);
        localStorage.setItem('ia_api_url', cfg.customApiUrl);
      }
      setLoadingConfig(false);
    });
    loadAllIaConfigs();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (iaProvider === 'ollama') {
      setOllamaModelLoading(true);
      fetchOllamaModels(customApiUrl).then(models => {
        setOllamaModels(models);
        setOllamaModelLoading(false);
      });
    }
    // eslint-disable-next-line
  }, [iaProvider, customApiUrl]);

  async function loadAllIaConfigs() {
    setLoadConfigsLoading(true);
    const uuid7 = getUserId();
    const token = window.localStorage.getItem('token');
    try {
      const res = await api.get(`/api/config/configs/${uuid7}?prefix=ia_config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const configs = res.data
        .filter(cfg => cfg.key === 'ia_config_global' || cfg.key.startsWith('ia_config_'))
        .map(cfg => {
          try {
            return { ...JSON.parse(cfg.value), key: cfg.key, id: cfg.id };
          } catch {
            return { key: cfg.key, id: cfg.id };
          }
        });
      setIaConfigs(configs);
      if (configs.length > 0) {
        setSelectedConfigIdx(0);
        // Applique la config sélectionnée
        setIaProvider(configs[0].iaProvider || iaProvider);
        setIaModel(configs[0].iaModel || iaModel);
        setCustomApiUrl(configs[0].customApiUrl || customApiUrl);
      }
    } catch {}
    setLoadConfigsLoading(false);
  }

  function handleSelectIaConfig(idx) {
    setSelectedConfigIdx(idx);
    const cfg = iaConfigs[idx];
    if (cfg) {
      setIaProvider(cfg.iaProvider || iaProvider);
      setIaModel(cfg.iaModel || iaModel);
      setCustomApiUrl(cfg.customApiUrl || customApiUrl);
      localStorage.setItem('ia_provider', cfg.iaProvider || iaProvider);
      localStorage.setItem('ia_model', cfg.iaModel || iaModel);
      localStorage.setItem('ia_api_url', cfg.customApiUrl || customApiUrl);
    }
  }

  function handleEditIaConfig(cfg) {
    setIaProvider(cfg.iaProvider || '');
    setIaModel(cfg.iaModel || '');
    setCustomApiUrl(cfg.customApiUrl || '');
    setConfigName(cfg.key.replace(/^ia_config_global$/, 'Par défaut').replace(/^ia_config_/, ''));
    setEditingKey(cfg.key);
    setTab(1); // Bascule sur l'onglet Config
  }

  async function handleSendPrompt(idx) {
    setPrompts(prompts => {
      const newPrompts = [...prompts];
      newPrompts[idx].output = '';
      newPrompts[idx].loading = true;
      newPrompts[idx].error = '';
      return newPrompts;
    });
    let prompt = prompts[idx].input;
    const prevIdx = prompts[idx].prevPromptIdx;
    if (typeof prevIdx === 'number' && prevIdx >= 0 && prevIdx < idx) {
      const prevPrompt = prompts[prevIdx].input;
      prompt = prevPrompt + '\n' + prompt;
    }
    try {
      setPrompts(prompts => {
        const newPrompts = [...prompts];
        newPrompts[idx].loading = true;
        newPrompts[idx].error = '';
        return newPrompts;
      });
      const payload = {
        model: iaModel,
        messages: [{ role: 'user', content: prompt }],
        stream: false
      };
      const res = await proxyIaApiRequest({
        target_url: iaProvider === 'ollama' ? customApiUrl.replace(/\/generate$/, '/chat') : customApiUrl,
        payload,
        headers: { 'Content-Type': 'application/json' }
      });
      let output = '';
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await res.json();
        output = data.choices?.[0]?.message?.content || data.candidates?.[0]?.content || data.generated_text || data.message?.content || data.response || data.message || JSON.stringify(data);
      } else {
        const text = await res.text();
        try {
          const data = JSON.parse(text);
          output = data.choices?.[0]?.message?.content || data.candidates?.[0]?.content || data.generated_text || data.message?.content || data.response || data.message || text;
        } catch {
          output = text;
        }
      }
      setPrompts(prompts => {
        const newPrompts = [...prompts];
        newPrompts[idx].output = output;
        newPrompts[idx].loading = false;
        return newPrompts;
      });
    } catch (err) {
      setPrompts(prompts => {
        const newPrompts = [...prompts];
        newPrompts[idx].error = err.message || 'Erreur API';
        newPrompts[idx].loading = false;
        return newPrompts;
      });
    }
  }

  async function handleSendPromptsCascade(idx) {
    for (let i = 0; i <= idx; i++) {
      // Si déjà une réponse, on saute sauf si loading ou input changé
      if (prompts[i].loading) return;
      setPrompts(prompts => {
        const newPrompts = [...prompts];
        newPrompts[i].output = '';
        newPrompts[i].loading = true;
        newPrompts[i].error = '';
        return newPrompts;
      });
      let prompt = prompts[i].input;
      const prevIdx = prompts[i].prevPromptIdx;
      if (typeof prevIdx === 'number' && prevIdx >= 0 && prevIdx < i) {
        const prevPrompt = prompts[prevIdx].input;
        prompt = prevPrompt + '\n' + prompt;
      }
      let output = '';
      try {
        const payload = {
          model: iaModel,
          messages: [{ role: 'user', content: prompt }],
          stream: false
        };
        const res = await proxyIaApiRequest({
          target_url: iaProvider === 'ollama' ? customApiUrl.replace(/\/generate$/, '/chat') : customApiUrl,
          payload,
          headers: { 'Content-Type': 'application/json' }
        });
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const data = await res.json();
          output = data.choices?.[0]?.message?.content || data.candidates?.[0]?.content || data.generated_text || data.message?.content || data.response || data.message || JSON.stringify(data);
        } else {
          const text = await res.text();
          try {
            const data = JSON.parse(text);
            output = data.choices?.[0]?.message?.content || data.candidates?.[0]?.content || data.generated_text || data.message?.content || data.response || data.message || text;
          } catch { output = text; }
        }
        setPrompts(prompts => {
          const newPrompts = [...prompts];
          newPrompts[i].output = output;
          newPrompts[i].loading = false;
          return newPrompts;
        });
      } catch (err) {
        setPrompts(prompts => {
          const newPrompts = [...prompts];
          newPrompts[i].error = err.message || 'Erreur API';
          newPrompts[i].loading = false;
          return newPrompts;
        });
        break;
      }
    }
  }

  function handleAddPrompt() {
    setPrompts([...prompts, { input: '', output: '', loading: false, error: '', inputType: 'custom', outputType: 'text', prevPromptIdx: null }]);
  }
  function handleRemovePrompt(idx) {
    if (prompts.length === 1) return;
    setPrompts(prompts.filter((_, i) => i !== idx));
  }
  function handleInputChange(idx, value) {
    setPrompts(prompts => prompts.map((p, i) => i === idx ? { ...p, input: value } : p));
  }
  function handleInputTypeChange(idx, value) {
    setPrompts(prompts => prompts.map((p, i) => i === idx ? { ...p, inputType: value } : p));
  }
  function handleOutputTypeChange(idx, value) {
    setPrompts(prompts => prompts.map((p, i) => i === idx ? { ...p, outputType: value } : p));
  }
  function handleOutputToInput(idx) {
    if (prompts[idx].output) {
      handleInputChange(idx + 1, prompts[idx].output);
    }
  }

  function handleIaProviderChange(event, value) {
    if (!value) return;
    setIaProvider(value.value);
    setIaModel(value.models[0]);
    setCustomApiUrl(value.aiUrl || '');
    localStorage.setItem('ia_provider', value.value);
    localStorage.setItem('ia_model', value.models[0]);
    localStorage.setItem('ia_api_url', value.aiUrl || '');
    setConfigSaved(false);
  }
  function handleIaModelChange(event, value) {
    if (!value) return;
    setIaModel(value);
    localStorage.setItem('ia_model', value);
    setConfigSaved(false);
  }
  function handleCustomApiUrlChange(e) {
    setCustomApiUrl(e.target.value);
    localStorage.setItem('ia_api_url', e.target.value);
    setConfigSaved(false);
  }

  async function handleSaveConfig(e) {
    e.preventDefault();
    const key = editingKey || (configName ? `ia_config_${configName.replace(/\s+/g, '_').toLowerCase()}` : 'ia_config_global');
    await saveIaConfigForUser({ iaProvider, iaModel, customApiUrl, key });
    setConfigSaved(true);
    setTimeout(() => setConfigSaved(false), 2000);
    setEditingKey(null);
    loadAllIaConfigs();
  }

  function handleFileInputChange(idx, e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      handleInputChange(idx, event.target.result);
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  function handleOpenFileDialog(idx) {
    if (fileInputRefs.current[idx]) fileInputRefs.current[idx].click();
  }

  // Suppression d'une config IA enregistrée
  async function handleDeleteIaConfig(cfg) {
    const uuid7 = getUserId();
    if (!uuid7 || !cfg.key) return;
    const token = window.localStorage.getItem('token');
    try {
      await api.delete(`/api/config/user/ia-config?uuid7=${encodeURIComponent(uuid7)}&key=${encodeURIComponent(cfg.key)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadAllIaConfigs();
    } catch {}
  }

  // Ajoute une fonction pour charger une session de prompts depuis un fichier backend
  async function handleLoadPromptsFromFile() {
    const userStr = window.localStorage.getItem('user');
    let uuid7 = null;
    if (userStr) {
      try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
    }
    if (!uuid7) { alert('Utilisateur non trouvé'); return; }
    const token = window.localStorage.getItem('token');
    const path = window.prompt('Nom du fichier à charger ?', 'prompts-YYYY-MM-DD-HH-mm-ss.json');
    if (!path) return;
    try {
      const res = await api.get(`/file?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(path)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const loadedPrompts = JSON.parse(res.data.content);
      if (!Array.isArray(loadedPrompts)) throw new Error('Format de fichier invalide');
      setPrompts(loadedPrompts);
      alert('Session de prompts chargée !');
    } catch (e) {
      alert("Erreur lors du chargement des prompts : " + (e?.message || ''));
    }
  }

  // Explorer-like dialog to select a prompt session file from backend
  async function fetchExplorerFiles(path) {
    setExplorerLoading(true);
    setExplorerError('');
    try {
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      const token = window.localStorage.getItem('token');
      let relPath = path === '/' ? '' : path.replace(/^\//, '');
      const res = await api.get(`/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExplorerFiles(res.data);
    } catch (e) {
      setExplorerError("Erreur lors du chargement des fichiers.");
      setExplorerFiles([]);
    } finally {
      setExplorerLoading(false);
    }
  }

  function handleOpenFromExplorer() {
    setExplorerOpen(true);
    setExplorerPath('/');
    fetchExplorerFiles('/');
  }

  async function handleExplorerSelectFile(file) {
    // Lecture backend
    try {
      setExplorerLoading(true);
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      const token = window.localStorage.getItem('token');
      let relPath = explorerPath === '/' ? file.name : explorerPath.replace(/^\//, '') + '/' + file.name;
      const res = await api.get(`/file?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const loadedPrompts = JSON.parse(res.data.content);
      if (!Array.isArray(loadedPrompts)) throw new Error('Format de fichier invalide');
      setPrompts(loadedPrompts);
      setExplorerOpen(false);
      alert('Session de prompts chargée !');
    } catch (e) {
      setExplorerError("Erreur lors de la lecture du fichier.");
    } finally {
      setExplorerLoading(false);
    }
  }

  function handleExplorerNavigate(folder) {
    const newPath = explorerPath === '/' ? `/${folder}` : `${explorerPath}/${folder}`;
    setExplorerPath(newPath);
    fetchExplorerFiles(newPath);
  }

  function handleExplorerGoUp() {
    if (explorerPath === '/') return;
    const parts = explorerPath.split('/').filter(Boolean);
    parts.pop();
    const newPath = parts.length === 0 ? '/' : '/' + parts.join('/');
    setExplorerPath(newPath);
    fetchExplorerFiles(newPath);
  }

  return (
    <Box sx={{ height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column' }}>
      <Paper elevation={3} sx={{
        p: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        width: '100vw',
        height: '100vh',
        borderRadius: 0
      }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab icon={<SmartToyIcon />} label="Agent IA" />
          <Tab icon={<SettingsIcon />} label="Configuration" />
        </Tabs>
        {tab === 0 && (
          <>
            {iaConfigs.length > 1 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Choisir une configuration IA sauvegardée :</Typography>
                <TextField
                  select
                  value={selectedConfigIdx}
                  onChange={e => handleSelectIaConfig(Number(e.target.value))}
                  size="small"
                  sx={{ width: 320, mt: 1 }}
                >
                  {iaConfigs.map((cfg, idx) => (
                    <MenuItem key={cfg.id || idx} value={idx}>
                      {cfg.iaProvider || 'IA'} - {cfg.iaModel || ''} {cfg.customApiUrl ? '(' + cfg.customApiUrl + ')' : ''}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <SmartToyIcon color="primary" fontSize="large" />
              <Typography variant="h5">Agent IA en cascade</Typography>
            </Box>
            <Box sx={{ overflowY: 'auto', maxHeight: 480, pr: 1 }}>
              {prompts.map((p, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f8f8fa', position: 'relative' }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="subtitle2" color="primary">Prompt {idx + 1}</Typography>
                    {prompts.length > 1 && (
                      <Button size="small" onClick={() => handleRemovePrompt(idx)} color="error" startIcon={<DeleteIcon />} sx={{ ml: 1 }}>Supprimer</Button>
                    )}
                  </Box>
                  <Box display="flex" gap={2} mb={1}>
                    <TextField
                      select
                      label="Source de l'entrée"
                      value={p.inputType}
                      onChange={e => handleInputTypeChange(idx, e.target.value)}
                      sx={{ minWidth: 180 }}
                    >
                      {INPUT_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Format de sortie"
                      value={p.outputType}
                      onChange={e => handleOutputTypeChange(idx, e.target.value)}
                      sx={{ minWidth: 150 }}
                    >
                      {OUTPUT_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  {/* Sélection d'un prompt précédent */}
                  {idx > 0 && (
                    <TextField
                      select
                      label="Prompt précédent à combiner"
                      value={p.prevPromptIdx ?? ''}
                      onChange={e => {
                        setPrompts(prompts => prompts.map((pp, i) => i === idx ? { ...pp, prevPromptIdx: Number(e.target.value) } : pp));
                      }}
                      sx={{ minWidth: 220, mb: 2 }}
                    >
                      <MenuItem value="">Aucun</MenuItem>
                      {prompts.slice(0, idx).map((pp, j) => (
                        <MenuItem key={j} value={j}>{`Prompt ${j + 1}`}</MenuItem>
                      ))}
                    </TextField>
                  )}
                  {/* Zone de texte "Entrée" */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <TextField
                      label="Entrée"
                      value={p.input}
                      onChange={e => handleInputChange(idx, e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                      sx={{ mb: 0 }}
                      disabled={p.inputType === 'file'}
                    />
                    {p.inputType === 'file' && (
                      <>
                        <input
                          type="file"
                          accept=".txt,.md,.json,.js,.py,.csv,.log,.xml,.html,.css,.yaml,.yml,.ini,.sh,.bat,.c,.cpp,.java,.ts,.tsx,.jsx,.pdf"
                          style={{ display: 'none' }}
                          ref={el => fileInputRefs.current[idx] = el}
                          onChange={e => handleFileInputChange(idx, e)}
                        />
                        <Button onClick={() => handleOpenFileDialog(idx)} startIcon={<FolderOpenIcon />} variant="outlined">
                          Choisir un fichier
                        </Button>
                      </>
                    )}
                  </Box>
                  {/* Bouton envoyer */}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => idx === prompts.length - 1 ? handleSendPromptsCascade(idx) : handleSendPrompt(idx)}
                    disabled={p.loading || (p.inputType === 'custom' && !p.input.trim()) || (p.inputType === 'prev_output' && idx === 0) || (p.inputType === 'file' && !p.input)}
                  >
                    {p.loading ? <CircularProgress size={20} /> : 'Envoyer'}
                  </Button>
                  {/* Affichage de la sortie */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="secondary">Réponse IA :</Typography>
                    <Paper variant="outlined" sx={{ p: 2, minHeight: 80, bgcolor: '#fff', color: '#222', fontFamily: 'monospace', fontSize: 16 }}>
                      {p.loading ? (
                        <span style={{ color: '#888' }}>Réponse en cours...</span>
                      ) : p.error ? (
                        <span style={{ color: 'red' }}>{p.error}</span>
                      ) : p.output ? (
                        <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{p.output}</span>
                      ) : (
                        <span style={{ color: '#aaa' }}>Aucune réponse à afficher.</span>
                      )}
                    </Paper>
                    <Button size="small" sx={{ mt: 1 }} onClick={() => handleOutputToInput(idx)}>
                      Utiliser cette sortie comme entrée du prompt suivant
                    </Button>
                  </Box>
                </Paper>
              ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 2 }}>
              <Button variant="outlined" color="primary" onClick={handleOpenSaveExplorer}>
                Sauvegarder la session de prompts
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleOpenFromExplorer}>
                Ouvrir une session de prompts
              </Button>
            </Box>
            <Button onClick={handleAddPrompt} startIcon={<AddIcon />} variant="outlined" color="secondary">Ajouter un prompt</Button>
          </>
        )}
        {tab === 1 && (
          <form onSubmit={handleSaveConfig} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <SettingsIcon color="primary" fontSize="large" />
              <Typography variant="h6">Configuration IA</Typography>
            </Box>
            {loadingConfig ? (
              <Typography color="text.secondary" sx={{ mb: 2 }}>Chargement de la configuration…</Typography>
            ) : null}
            <Autocomplete
              options={IA_MODELS}
              value={IA_MODELS.find(opt => opt.value === iaProvider) || IA_MODELS[0]}
              onChange={handleIaProviderChange}
              getOptionLabel={opt => opt.label}
              renderOption={(props, option) => {
                // Ajoute un signe visuel (✓) si cette config est déjà enregistrée pour l'utilisateur
                const isSaved = iaConfigs.some(cfg => cfg.iaProvider === option.value && cfg.iaModel === iaModel && cfg.customApiUrl === customApiUrl);
                return (
                  <li {...props}>
                    {option.label} {isSaved && <span style={{color: '#2e7d32', marginLeft: 8}}>✓</span>}
                  </li>
                );
              }}
              renderInput={params => <TextField {...params} label="Fournisseur IA" />}
              sx={{ mb: 2 }}
              isOptionEqualToValue={(opt, val) => opt.value === val.value}
            />
            {iaProvider === 'ollama' ? (
              <Autocomplete
                freeSolo
                options={ollamaModels}
                loading={ollamaModelLoading}
                value={iaModel}
                onInputChange={(e, v) => setIaModel(v)}
                renderInput={(params) => (
                  <TextField {...params} label="Modèle Ollama" variant="outlined" fullWidth sx={{ mb: 2 }} />
                )}
              />
            ) : (
              <TextField
                select
                label="Modèle IA"
                value={iaModel}
                onChange={e => handleIaModelChange(null, e.target.value)}
                sx={{ mb: 2 }}
              >
                {(IA_MODELS.find(opt => opt.value === iaProvider)?.models || []).map(m => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Nom du profil IA"
              value={configName}
              onChange={e => setConfigName(e.target.value)}
              sx={{ mb: 2 }}
              required
              helperText="Ex : GPT-4, Ollama local, Gemini..."
            />
            <TextField
              label="URL de l'API"
              value={customApiUrl}
              onChange={handleCustomApiUrlChange}
              fullWidth
              required
              helperText="Exemple : http://localhost:11434/api/generate, https://api.openai.com/v1/chat/completions, ..."
              sx={{ mb: 2 }}
            />
            {iaConfigs.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Profils IA enregistrés :</Typography>
                {iaConfigs.map((cfg, idx) => (
                  <Box key={cfg.key} display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography>{cfg.key.replace(/^ia_config_global$/, 'Par défaut').replace(/^ia_config_/, '') || 'Par défaut'}</Typography>
                    <Button onClick={() => handleEditIaConfig(cfg)} size="small" color="primary" variant="outlined">Éditer</Button>
                    <Button onClick={() => handleDeleteIaConfig(cfg)} size="small" color="error" variant="outlined" startIcon={<DeleteIcon />}>Supprimer</Button>
                  </Box>
                ))}
              </Box>
            )}
            <Button type="submit" variant="contained" color="primary">Enregistrer</Button>
            {configSaved ? <Typography color="success.main" sx={{ mt: 1 }}>Configuration sauvegardée !</Typography> : null}
          </form>
        )}
      </Paper>
      <Dialog open={explorerOpen} onClose={() => setExplorerOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Ouvrir une session de prompts</DialogTitle>
        <DialogContent>
          {explorerLoading && <CircularProgress size={24} />}
          {explorerError && <Typography color="error">{explorerError}</Typography>}
          <Breadcrumbs sx={{ mb: 1 }}>
            <Button size="small" onClick={handleExplorerGoUp} disabled={explorerPath === '/'}>↑</Button>
            <Typography variant="caption">{explorerPath}</Typography>
          </Breadcrumbs>
          <List>
            {explorerFiles.map(file => (
              <ListItem button key={file.name} onClick={async () => file.type === 'folder' ? handleExplorerNavigate(file.name) : await handleExplorerSelectFile(file)}>
                <ListItemIcon>{file.type === 'folder' ? <FolderIcon /> : <InsertDriveFileIcon />}</ListItemIcon>
                <ListItemText primary={file.name} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExplorerOpen(false)}>Annuler</Button>
        </DialogActions>
      </Dialog>
      {/* Dialog d'exploration pour sauvegarder la session de prompts */}
      <Dialog open={saveExplorerOpen} onClose={() => setSaveExplorerOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Sauvegarder la session de prompts</DialogTitle>
        <DialogContent>
          {saveExplorerLoading && <CircularProgress size={24} />}
          {saveExplorerError && <Typography color="error">{saveExplorerError}</Typography>}
          <Breadcrumbs sx={{ mb: 1 }}>
            <Button size="small" onClick={handleSaveExplorerGoUp} disabled={saveExplorerPath === '/'}>↑</Button>
            <Typography variant="caption">{saveExplorerPath}</Typography>
          </Breadcrumbs>
          <List>
            {saveExplorerFiles.filter(f => f.type === 'folder').map(folder => (
              <ListItem button key={folder.name} onClick={() => handleSaveExplorerNavigate(folder.name)}>
                <ListItemIcon><FolderIcon /></ListItemIcon>
                <ListItemText primary={folder.name} />
              </ListItem>
            ))}
          </List>
          <TextField
            label="Nom du fichier"
            value={saveName}
            onChange={e => setSaveName(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveExplorerOpen(false)}>Annuler</Button>
          <Button onClick={handleSavePromptsToBackend} variant="contained">Sauvegarder</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
